import type { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import type { SalesforceTask } from '../../../../../domain/salesforceActivity';
import { StyledEmptyStateView } from '../../styles';
import { ActivityList } from '../ActivityList/ActivityList';
import { CategoryFilter } from '../CategoryFilter/CategoryFilter';
import { LoadingState } from '../LoadingState/LoadingState';

import { StyledSalesforceActivityContent, StyledViewInSalesforceButton } from './styles';

interface IProps {
  isLoading?: boolean;
  isExporting?: boolean;
  isEmpty?: boolean;
  isSalesforceEnabled?: boolean;
  isUserCanManageCRM?: boolean;
  isFoundButNotOwned?: boolean;
  filteredTasks: readonly SalesforceTask[];
  categories: readonly string[];
  selectedCategory: string;
  salesforceLink?: string;
  onSelectCategory: (category: string) => void;
  onExportToSalesforce: VoidFunction;
  onSalesforceLinkClick: VoidFunction;
  onManageCTAClick: VoidFunction;
}

export const SalesforceActivityContent = ({
  isLoading,
  isExporting,
  isEmpty,
  filteredTasks,
  isSalesforceEnabled,
  isFoundButNotOwned,
  isUserCanManageCRM,
  categories,
  selectedCategory,
  onSelectCategory,
  salesforceLink,
  onExportToSalesforce,
  onSalesforceLinkClick,
  onManageCTAClick,
}: IProps): ReactElement => {
  const { t } = useTranslation();

  const handleManagePageOpen = () => {
    onManageCTAClick();

    window.open(`https://pro.similarweb.com/#/sales/management`, '_blank');
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (!isSalesforceEnabled) {
    return (
      <StyledEmptyStateView
        variant="salesforce"
        img="/images/empty-state-salesforce.svg"
        textKey="company.salesforce_activity.not-enabled.empty-state.title"
        messageKey="company.salesforce_activity.not-enabled.empty-state.message"
        buttonKey={isUserCanManageCRM ? 'company.salesforce_activity.not-enabled.empty-state.button' : undefined}
        onButtonClick={handleManagePageOpen}
      />
    );
  }

  if (isFoundButNotOwned) {
    return (
      <StyledEmptyStateView
        variant="salesforceConnection"
        img="/images/salesforce-connection.png"
        textKey="company.salesforce_activity.not-owned.empty-state.title"
        messageKey="company.salesforce_activity.not-owned.empty-state.message"
        buttonKey="contacts.contact.export_to_crm.preview_modal.heading.sf"
        isButtonLoading={isExporting}
        onButtonClick={onExportToSalesforce}
      />
    );
  }

  if (isEmpty) {
    return (
      <StyledEmptyStateView
        variant="salesforce"
        img="/images/empty-state-salesforce.svg"
        textKey="company.salesforce_activity.no-data.empty-state.title"
        messageKey="company.salesforce_activity.no-data.empty-state.message"
        buttonKey="contacts.contact.export_to_crm.preview_modal.heading.sf"
        isButtonLoading={isExporting}
        onButtonClick={onExportToSalesforce}
      />
    );
  }

  return (
    <StyledSalesforceActivityContent>
      <CategoryFilter categories={categories} selectedCategory={selectedCategory} onCategoryChange={onSelectCategory} />
      <ActivityList tasks={filteredTasks} />
      <StyledViewInSalesforceButton link={salesforceLink} onClick={onSalesforceLinkClick}>
        {t('company.salesforce_activity.view_in_salesforce')}
      </StyledViewInSalesforceButton>
    </StyledSalesforceActivityContent>
  );
};
